import merge                               from 'helpful-merge';
import {Observable, Subject, Subscription} from 'rxjs';

import ConvivaAnalytics          from '../Analytics/Conviva/ConvivaAnalytics';
import IInteractionPayload       from '../Analytics/Interactions/Interfaces/IInteractionPayload';
import IAnalytics                from '../Analytics/Shared/Interfaces/IAnalytics';
import {VERSION}                 from '../Shared/Constants/Build';
import ErrorType                 from '../Shared/Constants/ErrorType';
import ISolution                 from '../Solutions/Shared/Interfaces/ISolution';
import solutionFactory           from '../Solutions/solutionFactory';
import connectStoreSubscriptions from '../State/connectStoreSubscriptions';
import createState               from '../State/createState';
import IPlayerState              from '../State/Shared/Interfaces/IPlayerState';
import ISnapshot                 from '../State/Shared/Interfaces/ISnapshot';
import Config                    from '../State/Stores/Config/Config';
import IConfig                   from '../State/Stores/Config/Interfaces/IConfig';
import MediaType                 from '../State/Stores/Media/Constants/MediaType';
import Media                     from '../State/Stores/Media/Media';
import renderTestId              from '../Ui/Browser/Util/renderTestId';
import * as TestIds              from '../Ui/Shared/Constants/TestIds';
import IUi                       from '../Ui/Shared/Interfaces/IUi';
import uiFactory                 from '../Ui/uiFactory';

import connectGlobalEvents        from './connectGlobalEvents';
import connectPlayerSubscriptions from './connectPlayerSubscriptions';
import ConsumerInput              from './ConsumerInput';
import ConsumerOutput             from './ConsumerOutput';
import IErrorsPayload             from './Interfaces/IErrorsPayload';
import IEventBinding              from './Interfaces/IEventBinding';
import IMedia                     from './Interfaces/IMedia';
import IPlayer                    from './Interfaces/IPlayer';
import * as styles                from './player.css';
import Refs                       from './Refs';

/**
 * A class acting as a "mediator" between all domains of the application,
 * and the primary entry point into it.
 *
 * Each instance holds self-contained instances of the player state,
 * solution, and UI, and manages the subscribing and unsubscribing of
 * streams connecting the various domains.
 */

class Player implements IPlayer {
    public solution: ISolution = null;
    public ui: IUi = null;
    public refs: Refs = null;
    public state: IPlayerState = null;
    public consumerInput = new ConsumerInput();
    public consumerOutput = new ConsumerOutput();
    public addEventListener: EventTarget['addEventListener'];
    public removeEventListener: EventTarget['removeEventListener'];
    public dispatchEvent: EventTarget['dispatchEvent'];
    public analytics: IAnalytics;

    private subscriptions: Subscription[] = [];
    private eventBindings: IEventBinding[] = [];
    private eventTarget = document.createDocumentFragment();

    constructor(wrapper: HTMLElement, options: IConfig) {
        this.state = createState(this.consumerOutput.interactions$, {config: options});
        this.refs = this.buildSkeleton(wrapper);
        this.consumerOutput.state$ = this.state.update$;
        this.addEventListener = this.eventTarget.addEventListener.bind(this.eventTarget);
        this.removeEventListener = this.eventTarget.removeEventListener.bind(this.eventTarget);
        this.dispatchEvent = this.eventTarget.dispatchEvent.bind(this.eventTarget);
        this.analytics = new ConvivaAnalytics(this);
    }

    public get state$(): Observable<ISnapshot> {
        return this.consumerOutput.state$;
    }

    public get interactions$(): Subject<IInteractionPayload> {
        return this.consumerOutput.interactions$;
    }

    public get errors$(): Subject<IErrorsPayload> {
        return this.consumerOutput.errors$;
    }

    public get hasLoadedSolution(): boolean {
        return this.solution !== null;
    }

    private get hasLoadedUi(): boolean {
        return this.ui !== null;
    }

    private get config(): Config {
        return this.state.stores.config.value;
    }

    /**
     * Validates provied media, loads it into the player, optionally
     * loading the appropriate solution and UI if not yet loaded.
     */

    public load(nextMediaRaw: IMedia): void {
        if (typeof nextMediaRaw.src !== 'string' || nextMediaRaw.src.length === 0) {
            throw new TypeError('Invalid media source provided');
        }

        const nextMedia: Media = merge(new Media(), nextMediaRaw);

        if (![
            MediaType.LINEAR,
            MediaType.LIVE,
            MediaType.VOD
        ].includes(nextMedia.mediaType)) {
            throw new RangeError(`Invalid media type "${nextMedia.mediaType}" provided`);
        }

        this.loadDependencies(nextMedia)
            .then(() => this.consumerInput.media$.next(nextMedia))
            .catch(err => console.error(err));
    }

    /**
     * Unloads the currently loaded media, and returns the player
     * to its initial "unloaded" state.
     */

    public unload(): void {
        this.consumerInput.media$.next({});
    }

    /**
     * Allows on-the-fly configuration of the player at any time, by updating the
     * `Config` store with one or more configuration options.
     */

    public configure(options: IConfig): void {
        // NB: This breaks the pattern of only the stores knowing about their own implementation
        // (e.g. actions interface), but is neccessary to allow on-the-fly configuration, before
        // a solution is loaded and subscriptions are created.

        this.state.stores.config.actions.configure(options);
    }

    /**
     * Restarts playback of the video if paused.
     */

    public play(): void {
        this.consumerInput.togglePlayPause$.next(true);
    }

    /**
     * Pauses playback of the video if playing.
     */

    public pause(): void {
        this.consumerInput.togglePlayPause$.next(false);
    }

    /**
     * Seeks playback to a provided time in seconds.
     */

    public seek(time: number): void {
        this.consumerInput.seek$.next(time);
    }

    /**
     * Adjusts volume to a provided level between 0 and 1.
     */

    public volume(level: number): void {
        this.consumerInput.volume$.next(level);
    }

    /**
     * Gradually reduces the current volume by 50%, over 1
     * second, to a minimum of 10%.
     */

    public fadeOutVolume(): void {
        // NB: Currently disabled until we re-evaluate this functionality
    }

    /**
     * Gradually increases the current volume by 100%, over 1 second,
     * to a maximum of 80%.
     */

    public fadeInVolume(): void {
        // NB: Currently disabled until we re-evaluate this functionality
    }

    /**
     * Shows the controls if hidden/idle.
     */

    public showControls(): void {
        this.consumerInput.toggleControls$.next(true);
    }

    /**
     * Hides the controls if active.
     */

    public hideControls(): void {
        this.consumerInput.toggleControls$.next(false);
    }

    /**
     * Brings the player into fullscreen if not fullscreened.
     */

    public enterFullscreen(): void {
        this.consumerInput.toggleFullscreen$.next(true);
    }

    /**
     * Exits fullscreen mode if already fullscreened.
     */

    public exitFullscreen(): void {
        this.consumerInput.toggleFullscreen$.next(false);
    }

    /**
     * Puts the player into an "errored" state to communicate
     * to the user that playback could not be recovered.
     */

    public error(errorType: ErrorType): void {
        this.consumerInput.error$.next(errorType);
    }

    /**
     * Destroys the player by unsubcribing from all streams, unmounting the UI, and
     * finally stopping and destroying the solution.
     */

    public async destroy(): Promise<void> {
        this.disconnectSubscriptions();

        if (this.ui) this.ui.unmount();

        if (this.solution) await this.solution.destroy();

        this.analytics.destroy();

        this.refs.wrapper.innerHTML = '';
    }

    /**
     * Asyncronously loads code-split dependencies via their respective
     * factories, then attaches subscriptions and mounts the UI.
     */

    private async loadDependencies(media: IMedia): Promise<void> {
        this.disconnectSubscriptions();

        const nextSolution = await solutionFactory(this, media);

        if (this.hasLoadedSolution && nextSolution !== this.solution) {
            await this.solution.destroy();
        }

        this.solution = nextSolution;

        if (!this.hasLoadedUi) {
            this.ui = await uiFactory(this);
        }

        const consumerSubscriptions = [
            this.solution.mediaErrors$.subscribe(payload => this.errors$.next(payload))
        ];

        this.subscriptions.push(
            ...connectPlayerSubscriptions(this),
            ...connectStoreSubscriptions(this),
            ...consumerSubscriptions);
        this.eventBindings.push(...connectGlobalEvents(this));

        this.ui.mount();
    }

    /**
     * Disconnects all active stream subscriptions, and garbage collects
     * references to them.
     */

    private disconnectSubscriptions(): void {
        this.subscriptions.forEach(subscription => subscription.unsubscribe());
        this.eventBindings.forEach(binding => binding.release());

        this.subscriptions.length =
        this.eventBindings.length = 0;
    }

    /**
     * Builds a "skeleton DOM" which is independant and agnostic to
     * the UI. This consists of 3 elements (root, solution, and UI) which
     * are neccessary for the mounting of the solution and UI.
     */

    private buildSkeleton(wrapper: HTMLElement): Refs {
        const refs = new Refs();
        const root = refs.root = document.createElement('div');
        const solution = refs.solution = document.createElement('div');
        const ui = refs.ui = document.createElement('div');

        const rootTestId = renderTestId(TestIds.PLAYER);
        const solutionTestId = renderTestId(TestIds.PLAYER_SOLUTION);
        const uiTestId = renderTestId(TestIds.PLAYER_UI);

        root.className = styles.player;
        root.tabIndex = this.config.behaviour.tabIndex;
        solution.className = styles.solution;
        ui.className = styles.ui;

        root.setAttribute('data-version', VERSION);
        root.appendChild(solution);
        root.appendChild(ui);

        wrapper.appendChild(root);

        refs.wrapper = wrapper as HTMLDivElement;

        if (rootTestId) root.setAttribute('data-test-id', rootTestId);
        if (solutionTestId) solution.setAttribute('data-test-id', solutionTestId);
        if (uiTestId) ui.setAttribute('data-test-id', uiTestId);

        return refs;
    }
}

export default Player;
